import React from 'react';
import { usePolicy } from '../../contexts/PolicyContext';
import { spelledNumber } from '../../constants/policy';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const TermsOfService: React.FC = () => {
  const policy = usePolicy();
  const hoursText = spelledNumber(policy.windowHours);
  const navigate = useNavigate();
  const lastUpdated = 'September 2026';

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/login');
    }
  };

  const S = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="mb-8">
      <h2 className="text-xl font-semibold text-clinic-dark font-body mb-4">{title}</h2>
      {children}
    </section>
  );
  const P = ({ children }: { children: React.ReactNode }) => <p className="text-clinic-muted mb-4">{children}</p>;
  const H3 = ({ children }: { children: React.ReactNode }) => <h3 className="text-lg font-medium text-clinic-dark mt-4 mb-2">{children}</h3>;
  const UL = ({ children }: { children: React.ReactNode }) => <ul className="list-disc list-inside text-clinic-muted space-y-1 mb-4">{children}</ul>;

  return (
    <div className="min-h-screen bg-clinic-surface">
      <header className="bg-white shadow-clinic-sm border-b border-clinic-primary-lighter">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button onClick={handleBack} className="inline-flex items-center text-clinic-muted hover:text-clinic-primary transition-colors">
              <ArrowLeftIcon className="h-5 w-5 mr-2" />Back
            </button>
            <img src="/logos/logo-primary.png" alt="Clinic Studios" className="h-8 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-card shadow-clinic p-8">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-clinic-gold/20 rounded-full flex items-center justify-center mr-4">
              <DocumentTextIcon className="h-6 w-6 text-clinic-gold" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-clinic-dark font-body">Terms of Service</h1>
              <p className="text-sm text-clinic-muted">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="prose max-w-none text-clinic-dark">
            <P>These Terms of Service govern your access to and use of the Clinic Studios website, platform, booking system, and physical facilities. By creating an account, booking space, or accessing Clinic Studios, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, you may not use our services.</P>

            <S title="1. Service Description">
              <P>Clinic Studios ("we," "our," or "us") provides a professional workspace and booking platform that enables independent mental health and allied professionals ("Members") to reserve meeting rooms and manage scheduling within Clinic Studios facilities.</P>
              <P>Our services include:</P>
              <UL>
                <li>Online appointment booking and management tools</li>
                <li>Meeting room reservation and scheduling</li>
                <li>Optional Member directory and public profile management</li>
                <li>Administrative communication tools supporting scheduling</li>
                <li>Secure access control systems</li>
                <li>Billing and payment processing for Member room usage and membership fees</li>
              </UL>
              <P>Clinic Studios provides workspace infrastructure and administrative tools only. Clinic Studios does not:</P>
              <UL>
                <li>Provide medical, psychological, counseling, or healthcare services</li>
                <li>Act as a clinic of record</li>
                <li>Employ, supervise, or direct Members</li>
                <li>Provide clinical intake, documentation, or record keeping services</li>
                <li>Operate as a group practice</li>
                <li>Assume responsibility for Member services or professional outcomes</li>
              </UL>
              <P>All professional services are provided solely by independent Members.</P>
            </S>

            <S title="2. User Categories">
              <H3>Members</H3>
              <P>Independent mental health or allied professionals who hold an active membership and use Clinic Studios facilities.</P>
              <H3>Clients</H3>
              <P>Individuals who book appointments with independent Members.</P>
              <H3>General Users</H3>
              <P>Individuals accessing the platform for informational or administrative purposes.</P>
              <P>Different provisions of these Terms apply depending on your role.</P>
            </S>

            <S title="3. Independent Member Status">
              <P>Members:</P>
              <UL>
                <li>Operate as independent professionals</li>
                <li>Determine their own services, fees, and availability</li>
                <li>Control whether their profile is public or private</li>
                <li>Control whether booking is enabled or requires approval</li>
                <li>Maintain their own professional liability insurance</li>
                <li>Maintain and manage their own client records in independent systems</li>
                <li>Are responsible for compliance with applicable laws and professional standards</li>
              </UL>
              <P>Membership does not create employment, agency, partnership, joint venture, or supervisory relationship. Members shall not represent Clinic Studios as their employer, clinic of record, or professional governing body.</P>
              <P>Clinic Studios does not guarantee professional competence and does not assume responsibility for Member services or outcomes.</P>
            </S>

            <S title="4. Public Profiles and Booking Controls">
              <P>Members may elect to make their professional profile publicly visible, keep their profile private, allow Clients to request appointments, allow Clients to self-book available time slots, or disable booking functionality at any time. Profile visibility and booking settings are controlled solely by the Member.</P>
              <P>Clinic Studios does not assign or match Clients, does not recommend one Member over another, does not guarantee booking availability, does not guarantee provision of professional services, and does not endorse Member services.</P>
              <P>Appointment requests and bookings submitted through the platform are transmitted to the selected Member. Acceptance, cancellation, rescheduling, and delivery of services are determined solely by the Member.</P>
              <div className="bg-clinic-cream/50 rounded-card p-4 border border-clinic-primary-lighter mb-4">
                <p className="text-clinic-dark"><strong>Important:</strong> The platform is not intended for emergencies. If you are experiencing a medical or psychological emergency, contact 911 or appropriate emergency services immediately.</p>
              </div>
            </S>

            <S title="5. User Responsibilities">
              <H3>All Users</H3>
              <UL>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the confidentiality of account credentials</li>
                <li>Notify us immediately of unauthorized account access</li>
                <li>Use the platform in compliance with applicable laws</li>
                <li>Treat other users with professionalism and respect</li>
              </UL>
              <H3>Members</H3>
              <UL>
                <li>Maintain valid licenses, certifications, or credentials where applicable</li>
                <li>Maintain appropriate professional liability insurance</li>
                <li>Keep availability calendars accurate and up to date</li>
                <li>Honor confirmed bookings or cancel with appropriate notice</li>
                <li>Comply with applicable healthcare, privacy, and regulatory obligations</li>
                <li>Accurately represent their qualifications and scope of practice</li>
              </UL>
              <H3>Clients</H3>
              <UL>
                <li>Arrive on time for scheduled appointments</li>
                <li>Provide accurate information as required by the Member</li>
                <li>Cancel or reschedule appointments with appropriate notice</li>
                <li>Pay applicable fees to the Member in a timely manner</li>
              </UL>
              <P>Clinic Studios is not responsible for disputes between Members and Clients.</P>
            </S>

            <S title="6. Booking Policies">
              <H3>Appointment Confirmation</H3>
              <P>Meeting room bookings are confirmed upon completion of the booking process through the platform. Client appointment confirmations relate solely to scheduling between the Client and the Member. Clinic Studios does not guarantee provision of professional services. Members may require approval before confirming Client bookings.</P>
              <H3>Client Cancellations and Member Responsibility</H3>
              <P>Clients may cancel or reschedule appointments with their Member at any time, subject to the Member's own cancellation policies.</P>
              <div className="bg-clinic-cream/50 rounded-card p-4 border border-clinic-primary-lighter mb-4">
                <p className="text-clinic-dark"><strong>Clinic Studios bills Members for reserved meeting room time based on confirmed room bookings, regardless of whether a Client attends the appointment.</strong></p>
                <p className="text-clinic-muted mt-2">If a Client cancels or reschedules within {hoursText} hours of the scheduled appointment time, the Member remains financially responsible for the reserved meeting room booking and associated room usage fees.</p>
                <p className="text-clinic-muted mt-2">If a Client cancels or reschedules more than {hoursText} hours before the scheduled appointment time, the meeting room booking is released and no room usage fee is charged for that booking.</p>
              </div>
              <P>Members are responsible for establishing cancellation policies that align with their financial obligations under these Terms and their Membership Agreement, rebooking the time slot with another Client where appropriate, and using the reserved space for administrative or professional purposes.</P>
              <P>Clinic Studios does not mediate disputes regarding professional cancellation fees between Members and Clients.</P>
              {policy.selfCancelEnabled && (
                <>
                  <H3>Member Cancellations</H3>
                  <P>Members may cancel their own meeting room bookings through the platform, including bookings made for their own use and Client appointments they are unable to attend.</P>
                  <P>A booking cancelled more than {hoursText} hours before its scheduled start time is released and is not billed. A booking cancelled within {hoursText} hours of its scheduled start time may still be cancelled, but the meeting room remains reserved for the Member and the associated room usage fees are billed in full.</P>
                </>
              )}
              {policy.graceMinutes > 0 && (
                <P>Any booking cancelled within {spelledNumber(policy.graceMinutes)} minutes of being made is treated as a correction: the meeting room is released and no room usage fee is charged, regardless of when the booking was scheduled to start.</P>
              )}
              <P>Clinic Studios monitors cancellation activity and may contact Members or Clients whose cancellation patterns interfere with fair access to meeting rooms.</P>
            </S>

            <S title="7. Payment Terms">
              <H3>Member Billing</H3>
              <P>Members are billed monthly for room usage and membership fees based on monthly membership fee, hourly room rates (peak, standard, off-peak), and usage tier discounts based on total monthly hours.</P>
              <P>Meeting room charges are incurred at the time of booking and are the responsibility of the Member, regardless of Client attendance. Payments are processed automatically using the payment method on file. All fees are subject to applicable taxes, including GST or HST.</P>
              <H3>Client Payments</H3>
              <P>Clients pay Members directly for professional services rendered. Clinic Studios does not process payments between Clients and Members for professional services unless expressly stated. Clinic Studios does not collect, hold, or disburse funds for clinical services.</P>
              <H3>Refunds</H3>
              <P>Refund requests for membership or room charges must be submitted in writing within thirty (30) days. Refunds are issued at Clinic Studios' reasonable discretion and in accordance with applicable consumer protection legislation.</P>
            </S>

            <S title="8. Privacy">
              <P>Your use of our services is governed by our <Link to="/privacy" className="text-clinic-primary hover:underline">Privacy Policy</Link>. Clinic Studios collects administrative personal information necessary for scheduling, facility management, billing, and security. Clinic Studios does not collect or store clinical records, treatment notes, or diagnostic information.</P>
              <P>Members are solely responsible for compliance with applicable privacy and confidentiality obligations relating to their professional services.</P>
            </S>

            <S title="9. Security and Monitoring">
              <P>For safety and operational purposes, certain non-clinical areas may be monitored by video. Clinical meeting rooms are not monitored. Access credentials are non-transferable. Users must not attempt to bypass security systems.</P>
            </S>

            <S title="10. Assumption of Risk and Premises Liability">
              <P>Use of Clinic Studios facilities is at your own risk. To the maximum extent permitted by law, Clinic Studios is not liable for loss or theft of personal property, injury occurring on the premises, disruptions in building services, or acts of other users.</P>
              <P>Clinic Studios is liable only for damages directly caused by its gross negligence or willful misconduct.</P>
            </S>

            <S title="11. Limitation of Liability">
              <P>To the maximum extent permitted by law, the platform and facilities are provided on an "as is" and "as available" basis. No warranties of any kind are provided. Clinic Studios' total liability shall not exceed the amount paid by you to Clinic Studios in the twelve months preceding the claim. Clinic Studios shall not be liable for indirect, incidental, special, or consequential damages.</P>
              <div className="bg-clinic-cream/50 rounded-card p-4 border border-clinic-primary-lighter mb-4">
                <p className="text-clinic-dark"><strong>Important:</strong> Clinic Studios does not provide medical advice, diagnosis, or treatment.</p>
              </div>
            </S>

            <S title="12. Indemnification">
              <P>You agree to indemnify and hold harmless Clinic Studios, its directors, officers, employees, and affiliates from claims, liabilities, damages, losses, or expenses arising from your use of the platform or facilities, your breach of these Terms, your violation of applicable laws, and in the case of Members, professional services provided to Clients. This indemnification survives termination.</P>
            </S>

            <S title="13. Termination">
              <H3>By Users</H3>
              <P>You may terminate your account at any time through account settings or by contacting us. Members must settle outstanding balances before account closure.</P>
              <H3>By Clinic Studios</H3>
              <P>We may suspend or terminate accounts that violate these Terms, engage in fraudulent or illegal activity, have outstanding unpaid balances, repeatedly violate booking policies, or pose a risk to other users or the platform.</P>
              <H3>Effect of Termination</H3>
              <P>Upon termination, access to the platform and facilities will be revoked. Existing bookings may be cancelled. Data retention will follow our Privacy Policy.</P>
            </S>

            <S title="14. Governing Law and Dispute Resolution">
              <P>These Terms are governed by the laws of the Province of Alberta and the federal laws of Canada applicable therein. Disputes shall first be attempted to be resolved through good-faith negotiation. Where legally permissible, unresolved disputes shall be submitted to binding arbitration in Calgary, Alberta. Nothing in this section limits rights under applicable consumer protection legislation.</P>
            </S>

            <S title="15. Changes to Terms">
              <P>We may update these Terms from time to time. Material changes will be communicated via email or platform notification. Continued use after changes take effect constitutes acceptance of the updated Terms.</P>
            </S>

            <S title="16. Contact Information">
              <P>For questions about these Terms of Service, please contact:</P>
              <div className="bg-clinic-cream/50 rounded-card p-4 border border-clinic-primary-lighter">
                <p className="text-clinic-dark">
                  <strong>Clinic Studios</strong><br />
                  Email: info@clinicstudios.ca<br />
                  Phone: (403) 471-8617<br />
                  Address: 30 Springborough Blvd SW Suite 214, Calgary, AB T3H 5M6
                </p>
              </div>
            </S>

            <section className="border-t border-clinic-primary-lighter pt-6">
              <p className="text-sm text-clinic-muted">
                By creating an account or using our services, you acknowledge that you have read,
                understood, and agree to be bound by these Terms of Service.
              </p>
            </section>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-clinic-primary-lighter mt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img src="/logos/logo-icon.png" alt="Clinic Studios" className="h-8 w-auto opacity-60" />
              <p className="ml-4 text-sm text-clinic-muted">&copy; {new Date().getFullYear()} Clinic Studios. All rights reserved.</p>
            </div>
            <div className="flex gap-4">
              <Link to="/privacy" className="text-sm text-clinic-muted hover:text-clinic-primary transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-sm text-clinic-primary font-medium">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
